import React from 'react';
import { Button as MIButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment';
import EdiText from 'react-editext';
import NumberFormat from 'react-number-format';
import Select, { ValueType } from 'react-select';

import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { PaymentCollector } from '../../../../../shared/enums/payment-collector.enum';
import { PaymentGateway } from '../../../../../shared/enums/payment-gateway.enum';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { PaymentType } from '../../../../../shared/enums/payment-type.enum';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { hasAccess } from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { updateOnePayment } from '../../../../shared/services/data.service';
import {
  collectorOption,
  getPaymentStatusColor,
  paymentStatusOptions,
  paymentTypeOptions
} from '../../reservation-edit.helper';
import { PaymentStatusBadge } from '../payments-section/payment-status-badge.component';

interface PaymentDetailsModalProps {
  showModal: boolean;
  payment: PaymentModel | null;
  reservation: ReservationDetailsUiModel;
  isPermited: (roles: UserRole[]) => boolean;
  onClose: () => void;
  onGetOneReservation: (id: string) => Promise<any>;
  onStatusChange: (payment: PaymentModel, newStatus: PaymentStatus) => void;
}

function getCurrencyPrefix(currency?: string): string {
  return currency ? `${CURRENCY[currency as keyof typeof CURRENCY]} ` : 'EGP ';
}

export function PaymentDetailsModal({
  showModal,
  payment,
  reservation,
  isPermited,
  onClose,
  onGetOneReservation,
  onStatusChange
}: PaymentDetailsModalProps): JSX.Element | null {
  if (!payment) return null;

  const paymentStatusColourStyles = (stateValue: any) => {
    return {
      singleValue: (styles: any) => ({ ...styles, color: 'inherit' }),
      control: (styles: any) => {
        const colorObj = getPaymentStatusColor(stateValue);
        return {
          ...styles,
          ...colorObj
        };
      },
      option: (styles: any, { data }: any) => {
        const colorObj = getPaymentStatusColor(data.value);
        return {
          ...styles,
          ...colorObj
        };
      }
    };
  };

  const updateFeeValue = (idx: number, feeValue: any) => {
    const updatedPaymentItems = [...payment.paymentItems];
    updatedPaymentItems[idx] = { ...updatedPaymentItems[idx], feeValue };
    const promise = updateOnePayment({ id: payment.id, document: updatedPaymentItems }).then(
      (response) => {
        if (isErrorInfo(response)) throw response;
        return onGetOneReservation(reservation.id);
      }
    );
    birdToast.promise(promise, {
      loadingMessage: 'Updating price...',
      successMessage: 'Price updated successfully',
      errorPermissionMessage: 'You do not have permission to update this price',
      errorFailedMessage: 'Failed to update price'
    });
  };

  const isEditable =
    payment.paymentAmount > 0 &&
    !(
      payment.status === PaymentStatus.PARTIALLY_COLLECTED &&
      !isPermited([
        UserRole.FINANCE_MANAGER,
        UserRole.FINANCE_SPECIALIST,
        UserRole.ADMIN,
        UserRole.SUPER_ADMIN
      ])
    );

  return (
    <Modal
      config={{ showModal, fullScreen: false, showClose: true, maxWidth: 'lg' }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div style={{ padding: '24px' }}>
        <h3
          style={{
            margin: '0 0 20px 0',
            fontSize: '18px',
            fontWeight: 600,
            color: '#111827'
          }}
        >
          Payment Details - {payment.id}
        </h3>

        {/* Payment info summary */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px'
          }}
        >
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Status
            </div>
            {payment.paymentAmount > 0 ? (
              <Select
                styles={paymentStatusColourStyles(payment.status)}
                isDisabled={payment.paymentAmount <= 0}
                options={
                  payment.direction === 'out'
                    ? [{ label: 'Refunded', value: PaymentStatus.REFUNDED }]
                    : [
                        { label: 'Paid', value: PaymentStatus.COLLECTED },
                        ...(isPermited([
                          UserRole.FINANCE_MANAGER,
                          UserRole.FINANCE_SPECIALIST,
                          UserRole.ADMIN,
                          UserRole.SUPER_ADMIN
                        ])
                          ? [
                              {
                                label: 'Partially Collected',
                                value: PaymentStatus.PARTIALLY_COLLECTED
                              }
                            ]
                          : []),
                        ...(isPermited([
                          UserRole.RESERVATION_MANAGER,
                          UserRole.ADMIN,
                          UserRole.SUPER_ADMIN
                        ])
                          ? [
                              {
                                label: 'Pending',
                                value: PaymentStatus.PENDING
                              }
                            ]
                          : [])
                      ]
                }
                defaultValue={paymentStatusOptions.find((val) => val.value === payment.status)}
                onChange={(
                  value: ValueType<{ value: string | undefined; label: string }, false>
                ) => {
                  const newStatus =
                    (value && (value.value as PaymentStatus)) || PaymentStatus.COLLECTED;
                  onStatusChange(payment, newStatus);
                }}
              />
            ) : (
              <PaymentStatusBadge status={payment.status} />
            )}
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Amount
            </div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
              <NumberFormat
                value={payment.paymentAmount || 0}
                displayType={'text'}
                thousandSeparator={true}
                prefix={getCurrencyPrefix(payment.currency)}
              />
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Direction
            </div>
            <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>
              {payment.direction === 'out' ? 'PAY-OUT' : 'PAY-IN'}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Due Date
            </div>
            <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>
              {new Date(payment.dueDate).toDateString()}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Type
            </div>
            <Select
              className="min-w-[11rem]"
              isDisabled={!isEditable}
              options={paymentTypeOptions}
              defaultValue={paymentTypeOptions.find((val) => val.value === payment.paymentType)}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                updateOnePayment({
                  id: payment.id,
                  paymentType: value ? (value.value as PaymentType) : payment.paymentType
                }).then(() => onGetOneReservation(reservation.id));
              }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Collector
            </div>
            <Select
              className="min-w-[11rem]"
              isDisabled={!isEditable}
              options={collectorOption}
              defaultValue={collectorOption.find((val) => val.value === payment.collector)}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                updateOnePayment({
                  id: payment.id,
                  collector: value ? (value.value as PaymentCollector) : payment.collector
                }).then(() => onGetOneReservation(reservation.id));
              }}
            />
          </div>
        </div>

        {/* Updated info */}
        {(payment.status === PaymentStatus.COLLECTED ||
          payment.status === PaymentStatus.PARTIALLY_COLLECTED ||
          payment.status === PaymentStatus.REFUNDED) && (
          <div
            style={{
              marginBottom: '24px',
              padding: '12px 16px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            {payment.updatedBy && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Updated by: </span>
                <span style={{ fontWeight: 600, color: '#111827' }}>{payment.updatedBy}</span>
              </div>
            )}
            {payment.updatedAt && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Updated at: </span>
                <span style={{ fontWeight: 600, color: '#111827' }}>
                  {moment(payment.updatedAt).format('DD/MM/YYYY, h:mm:ss a')}
                </span>
              </div>
            )}
            {payment.status === PaymentStatus.PARTIALLY_COLLECTED && payment.partialPayment && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Partial Amount: </span>
                <span style={{ fontWeight: 600, color: '#111827' }}>
                  <NumberFormat
                    value={payment.partialPayment.amount || 0}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={getCurrencyPrefix(payment.partialPayment.currency)}
                  />
                </span>
              </div>
            )}
            {(payment.partialPayment || payment.bankCharges) && (
              <>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#6B7280' }}>Bank Charges: </span>
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    <NumberFormat
                      value={(payment.partialPayment?.bankCharges ?? payment.bankCharges) || 0}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={getCurrencyPrefix(
                        payment.partialPayment?.bankChargesCurrency || payment.bankChargesCurrency
                      )}
                    />
                  </span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#6B7280' }}>Forex Difference: </span>
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    <NumberFormat
                      value={
                        (payment.partialPayment?.forexDifference ?? payment.forexDifference) || 0
                      }
                      displayType={'text'}
                      thousandSeparator={true}
                    />
                  </span>
                </div>
              </>
            )}
            {payment.receipt?.receiptUrl && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Proof of Payment: </span>
                <a
                  href={payment.receipt.receiptUrl}
                  style={{ color: '#237c58', textDecoration: 'underline', fontWeight: 600 }}
                >
                  {payment.receipt.receiptId}
                </a>
              </div>
            )}
            {payment.partialPaymentReceipt?.receiptUrl && (
              <div>
                <span style={{ color: '#6B7280' }}>Partial Receipt: </span>
                <a
                  href={payment.partialPaymentReceipt.receiptUrl}
                  style={{ color: '#237c58', textDecoration: 'underline', fontWeight: 600 }}
                >
                  {payment.partialPaymentReceipt.receiptId}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Fee breakdown table */}
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
          Fee Breakdown
        </div>
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              padding: '10px 16px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '12px',
              fontWeight: 600,
              color: '#6B7280',
              textTransform: 'uppercase'
            }}
          >
            <div>Fee Name</div>
            <div>Type</div>
            <div>Amount</div>
          </div>
          {payment.paymentItems && payment.paymentItems.length > 0 ? (
            payment.paymentItems.map((paymentItem, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  padding: '12px 16px',
                  borderBottom:
                    idx < payment.paymentItems.length - 1 ? '1px solid #f3f4f6' : 'none',
                  fontSize: '14px',
                  color: '#374151',
                  alignItems: 'center'
                }}
              >
                <div>
                  {paymentItem.feeName === 'Unit Price' &&
                  paymentItem.feeValue &&
                  paymentItem.duration
                    ? `${paymentItem.feeValue / paymentItem.duration}x${paymentItem.duration}`
                    : paymentItem.feeName}
                </div>
                <div>{paymentItem.itemType || 'N/A'}</div>
                <div>
                  {(payment.status === PaymentStatus.OVERDUE ||
                    payment.status === PaymentStatus.PENDING) &&
                  hasAccess([PERMISSIONS_LIST.paymentsFullAccess]) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <EdiText
                        type="number"
                        value={`${paymentItem.feeValue}`}
                        onSave={(val) => {
                          updateFeeValue(idx, val);
                        }}
                      />
                      <MIButton
                        aria-controls="area-menu"
                        aria-haspopup="true"
                        onClick={() => {
                          updateFeeValue(idx, 0);
                        }}
                      >
                        <DeleteIcon fontSize="small" color={'error'} />
                      </MIButton>
                    </div>
                  ) : (payment.status === PaymentStatus.OVERDUE ||
                      payment.status === PaymentStatus.PENDING) &&
                    hasAccess([PERMISSIONS_LIST.paymentsDeleteAccess]) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <NumberFormat
                        value={paymentItem.feeValue}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={getCurrencyPrefix(paymentItem.currency)}
                      />
                      <MIButton
                        aria-controls="area-menu"
                        aria-haspopup="true"
                        onClick={() => {
                          updateFeeValue(idx, 0);
                        }}
                      >
                        <DeleteIcon fontSize="small" color={'error'} />
                      </MIButton>
                    </div>
                  ) : (
                    <NumberFormat
                      value={paymentItem.feeValue}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={getCurrencyPrefix(paymentItem.currency)}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}
            >
              No fee items
            </div>
          )}
        </div>

        {/* Online Attempts */}
        {payment.attempts && payment.attempts.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div
              style={{ fontSize: '14px', fontWeight: 600, color: '#237c58', marginBottom: '12px' }}
            >
              Online Attempts
            </div>
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                  padding: '10px 16px',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6B7280',
                  textTransform: 'uppercase'
                }}
              >
                <div>Status</div>
                <div>Card Number</div>
                <div>Attempt Date</div>
                <div>
                  {payment.paymentGateway === PaymentGateway.PAYMOB
                    ? 'Paymob Order ID'
                    : payment.paymentGateway === PaymentGateway.GEIDEA
                    ? 'Geidea ID'
                    : 'Payment Reference'}
                </div>
                <div>Reason</div>
              </div>
              {payment.attempts.map((attempt, aIdx) => (
                <div
                  key={aIdx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                    padding: '10px 16px',
                    borderBottom:
                      aIdx < (payment.attempts?.length || 0) - 1 ? '1px solid #f3f4f6' : 'none',
                    fontSize: '13px',
                    color: '#374151',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <img
                      style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                      src={
                        attempt.attemptStatus === 'completed'
                          ? `${PUBLIC_PATH}/assets/jpeg/payment-card-success.jpeg`
                          : attempt.attemptStatus === 'fraud'
                          ? `${PUBLIC_PATH}/assets/jpeg/danger.jpeg`
                          : `${PUBLIC_PATH}/assets/jpeg/failed-payment.webp`
                      }
                      alt={attempt.attemptStatus}
                    />
                    {attempt.attemptStatus}
                  </div>
                  <div>{attempt.cardNumber || 'N/A'}</div>
                  <div>{new Date(attempt.attemptDate).toDateString()}</div>
                  <div>
                    {(payment.paymentGateway === PaymentGateway.PAYMOB
                      ? attempt.paymobOrderId
                      : payment.paymentGateway === PaymentGateway.GEIDEA
                      ? attempt.invoiceId
                      : undefined) || 'N/A'}
                  </div>
                  <div>
                    {attempt.reason}
                    {attempt.reason === 'Approved' && payment.targetBankAccount
                      ? ' / ' + payment.targetBankAccount
                      : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
